/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: activities entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the activities table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the activities API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, activity_type_id, name, description, stage_id, status_id, is_blocking, estimated_duration, actual_duration, scheduled_date, completed_date, specifications, priority, verification_notes, photo_ids, signature_data, created_at, updated_at, created_by, start_date, end_date, trig_area_lot_id, trig_activity_type_name, trig_activity_type_color, trig_activity_status_name, trig_activity_status_color, trig_activity_stage_name, parent_activity_id, area_id, trig_area_stage_id, trig_lot_address, trig_lot_number, trig_builder_name, trig_lot_phase_name, trig_job_name, city, square_footage, square_footage_claimed, needs_follow_up, completed_by, department, trig_category_name, updated_by, trig_organization_id, trig_assigned_users, was_autocompleted, procedure_link, photo_required, trig_effective_photo_required]
 * Searchable fields: []
 * Filterable fields: [id, activity_type_id, stage_id, status_id, is_blocking, trig_area_lot_id, parent_activity_id, area_id, trig_area_stage_id, needs_follow_up, trig_organization_id, photo_required, trig_effective_photo_required]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  ActivitiesRow,
  ActivitiesInsert,
  ActivitiesUpdate
} from '../generated_schemas/generated-activities-schema.js';
import { ActivitiesQueryOptions } from '../generated_schemas/generated-activities-schema.js';
export type { ActivitiesQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface ActivitiesClientResponse {
  data: ActivitiesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for activities table operations
 *
 * @description Provides type-safe access to activities API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class ActivitiesClient {
  private readonly basePath = '/activities';

  /**
   * Get all activities records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of activities records
   */
  async getAll(options?: ActivitiesQueryOptions): Promise<ActivitiesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<ActivitiesClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single activities record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the activities record
   */
  async getById(id: string): Promise<ActivitiesRow> {
    const response = await httpClient.get<{
      data: ActivitiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new activities record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created activities record
   */
  async create(data: ActivitiesInsert): Promise<ActivitiesRow> {
    const response = await httpClient.post<{
      data: ActivitiesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing activities record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated activities record
   */
  async update(id: string, data: ActivitiesUpdate): Promise<ActivitiesRow> {
    const response = await httpClient.put<{
      data: ActivitiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a activities record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple activities records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created activities records
   */
  async createMany(data: ActivitiesInsert[]): Promise<ActivitiesRow[]> {
    const response = await httpClient.post<{
      data: ActivitiesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple activities records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search activities with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: ActivitiesQueryOptions
  ): Promise<ActivitiesClientResponse> {
    throw new Error('Search operations are not available for activities - no searchable fields configured');
  }

  /**
   * Get count of activities records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a activities record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'activities',
      publicFields: ['id', 'activity_type_id', 'name', 'description', 'stage_id', 'status_id', 'is_blocking', 'estimated_duration', 'actual_duration', 'scheduled_date', 'completed_date', 'specifications', 'priority', 'verification_notes', 'photo_ids', 'signature_data', 'created_at', 'updated_at', 'created_by', 'start_date', 'end_date', 'trig_area_lot_id', 'trig_activity_type_name', 'trig_activity_type_color', 'trig_activity_status_name', 'trig_activity_status_color', 'trig_activity_stage_name', 'parent_activity_id', 'area_id', 'trig_area_stage_id', 'trig_lot_address', 'trig_lot_number', 'trig_builder_name', 'trig_lot_phase_name', 'trig_job_name', 'city', 'square_footage', 'square_footage_claimed', 'needs_follow_up', 'completed_by', 'department', 'trig_category_name', 'updated_by', 'trig_organization_id', 'trig_assigned_users', 'was_autocompleted', 'procedure_link', 'photo_required', 'trig_effective_photo_required'],
      searchableFields: [],
      filterableFields: ['id', 'activity_type_id', 'stage_id', 'status_id', 'is_blocking', 'trig_area_lot_id', 'parent_activity_id', 'area_id', 'trig_area_stage_id', 'needs_follow_up', 'trig_organization_id', 'photo_required', 'trig_effective_photo_required'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'trig_area_lot_id', 'trig_activity_type_name', 'trig_activity_type_color', 'trig_activity_status_name', 'trig_activity_status_color', 'trig_activity_stage_name', 'trig_area_stage_id', 'trig_lot_address', 'trig_lot_number', 'trig_builder_name', 'trig_lot_phase_name', 'trig_job_name', 'city', 'trig_category_name', 'trig_organization_id', 'trig_assigned_users', 'trig_effective_photo_required'],
      requiredFields: [],
    };
  }
}

// Export singleton instance for convenience
export const activitiesClient = new ActivitiesClient();
